import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { UserPlus, Copy, Trash2, ToggleLeft, ToggleRight, Users, Mail, Calendar, QrCode, Shield } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import EmployeePermissionsDialog from "../components/EmployeePermissionsDialog";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function EmployeesManagementPage() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [invites, setInvites] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newRole, setNewRole] = useState("cashier");
  const [newPhone, setNewPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [createdCode, setCreatedCode] = useState(null);
  const [qrInvite, setQrInvite] = useState(null); // عرض QR لكود موجود
  const [permEmployee, setPermEmployee] = useState(null); // ✨ تعديل صلاحيات موظف

  const inviteUrl = (code) => `${window.location.origin}/employee-register?code=${encodeURIComponent(code)}`;

  const fetchData = useCallback(async () => {
    try {
      const [i, e] = await Promise.all([
        axios.get(`${API}/sub-accounts/invites`, { headers }),
        axios.get(`${API}/sub-accounts`, { headers }),
      ]);
      setInvites(i.data || []);
      setEmployees(e.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "خطأ في جلب البيانات");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const createInvite = async () => {
    if (!newName.trim()) return toast.error("أدخل اسم الموظف");
    setLoading(true);
    try {
      const r = await axios.post(`${API}/sub-accounts/invites`, {
        name: newName.trim(),
        role: newRole,
        phone: newPhone.trim() || null,
      }, { headers });
      setCreatedCode(r.data);
      setNewName("");
      setNewPhone("");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "خطأ");
    } finally {
      setLoading(false);
    }
  };

  const buildInviteText = (code) => {
    return `🎉 مرحباً! تم إنشاء حساب موظف لك في نظام حساباتي

📌 طريقتان للتسجيل:

🔗 الأسهل - اضغط الرابط مباشرة:
${inviteUrl(code)}

أو يدوياً:
1. ادخل على: ${window.location.origin}/employee-register
2. أدخل كود الدعوة: ${code}
3. أدخل إيميلك وكلمة سر جديدة

✅ بعدها تقدر تسجل دخول من نفس صفحة تسجيل الدخول الرئيسية`;
  };

  const sendWhatsApp = (code, phone) => {
    const text = buildInviteText(code);
    let cleanPhone = (phone || "").replace(/[^0-9]/g, "");
    // تنظيف الأرقام العراقية: 07XX → 9647XX
    if (cleanPhone.startsWith("07")) cleanPhone = "964" + cleanPhone.substring(1);
    else if (cleanPhone.startsWith("7") && cleanPhone.length === 10) cleanPhone = "964" + cleanPhone;
    const url = cleanPhone
      ? `https://wa.me/${cleanPhone}?text=${encodeURIComponent(text)}`
      : `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank");
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(buildInviteText(code));
    toast.success("✅ تم نسخ التعليمات");
  };

  const toggleEmployee = async (id) => {
    try {
      await axios.patch(`${API}/sub-accounts/${id}/toggle`, {}, { headers });
      toast.success("تم تحديث الحالة");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "خطأ");
    }
  };

  const deleteEmployee = async (id, name) => {
    if (!window.confirm(`هل تريد حذف الموظف "${name}" نهائياً؟`)) return;
    try {
      await axios.delete(`${API}/sub-accounts/${id}`, { headers });
      toast.success("تم الحذف");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "خطأ");
    }
  };

  const deleteInvite = async (id) => {
    if (!window.confirm("هل تريد حذف كود الدعوة؟")) return;
    try {
      await axios.delete(`${API}/sub-accounts/invites/${id}`, { headers });
      toast.success("تم الحذف");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "خطأ");
    }
  };

  const unusedInvites = invites.filter(i => !i.used);

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Users className="w-7 h-7 text-emerald-400" />
            إدارة الموظفين
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            أضف موظفين يقدرون يدخلون على حسابك بصلاحيات محددة
          </p>
        </div>
        <Button
          onClick={() => setShowCreate(true)}
          className="bg-emerald-600 hover:bg-emerald-700"
          data-testid="create-invite-btn"
        >
          <UserPlus className="w-4 h-4 me-2" />
          إضافة موظف جديد
        </Button>
      </div>

      {/* ✨ شريط إرشادي للمالك */}
      <Card className="bg-gradient-to-l from-cyan-900/20 to-blue-900/20 border-cyan-700/40">
        <CardContent className="p-4">
          <h3 className="text-cyan-300 font-semibold text-sm mb-2 flex items-center gap-1.5">
            📖 كيف يعمل نظام الموظفين؟
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-slate-300">
            <div className="bg-slate-900/40 rounded-lg p-3 border border-slate-700">
              <div className="text-cyan-400 font-bold mb-1">1️⃣ إضافة موظف</div>
              <p>اضغط "إضافة موظف جديد" → أدخل اسمه ودوره → احصل على كود دعوة + QR + رابط مباشر</p>
            </div>
            <div className="bg-slate-900/40 rounded-lg p-3 border border-slate-700">
              <div className="text-cyan-400 font-bold mb-1">2️⃣ تسجيل الموظف</div>
              <p>أرسل الرابط/QR للموظف عبر WhatsApp → يفتحه ويدخل إيميل وكلمة سر → ينشأ حسابه</p>
            </div>
            <div className="bg-slate-900/40 rounded-lg p-3 border border-slate-700">
              <div className="text-cyan-400 font-bold mb-1">3️⃣ دخول الموظف</div>
              <p>يدخل من <span className="font-mono text-emerald-400">/login</span> (نفس صفحتك) بإيميله وكلمة سره. يرى البيانات تحت حسابك بصلاحيات دوره فقط.</p>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-slate-700/50 text-xs text-slate-400">
            💡 <span className="text-cyan-300 font-semibold">تخصيص الصلاحيات:</span> اضغط أيقونة 🛡️ بجانب أي موظف في القائمة لتعديل صلاحياته بدقّة (مثلاً: السماح للكاشير برؤية أسعار التكلفة). يمكنك حفظ التشكيلة كقالب وتطبيقه على موظفين آخرين.
          </div>
        </CardContent>
      </Card>

      {/* أكواد دعوة غير مستخدمة */}
      {unusedInvites.length > 0 && (
        <Card className="bg-amber-900/20 border-amber-700/50">
          <CardHeader>
            <CardTitle className="text-amber-300 text-lg">
              🎫 أكواد دعوة بانتظار الاستخدام ({unusedInvites.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {unusedInvites.map((inv) => (
                <div key={inv.id} className="flex items-center justify-between p-3 rounded bg-slate-900/50 border border-amber-700/30">
                  <div className="flex items-center gap-3">
                    <div className="font-mono text-amber-400 font-bold text-lg">{inv.code}</div>
                    <div className="text-white">{inv.name}</div>
                    <div className="text-xs px-2 py-0.5 bg-slate-700 rounded text-slate-300">{inv.role}</div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="ghost" onClick={() => setQrInvite(inv)} className="text-cyan-400" title="عرض QR Code" data-testid={`qr-invite-${inv.id}`}>
                      <QrCode className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => sendWhatsApp(inv.code, inv.phone)} className="text-green-400" title="إرسال WhatsApp">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/></svg>
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => copyCode(inv.code)} className="text-emerald-400" title="نسخ التعليمات">
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => deleteInvite(inv.id)} className="text-red-400">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* قائمة الموظفين */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-lg">👥 الموظفين المسجلين ({employees.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {employees.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              لا يوجد موظفين بعد. اضغط "إضافة موظف جديد" لإنشاء كود دعوة.
            </div>
          ) : (
            <div className="space-y-2">
              {employees.map((e) => (
                <div key={e.id} className="flex items-center justify-between p-3 rounded bg-slate-900/50">
                  <div>
                    <div className="text-white font-bold">{e.name}</div>
                    <div className="text-sm text-slate-400 flex items-center gap-3 mt-1">
                      <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {e.email}</span>
                      <span className="px-2 py-0.5 bg-slate-700 rounded text-xs">{e.role}</span>
                      {!e.is_active && <span className="text-red-400 text-xs">معطّل</span>}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setPermEmployee(e)}
                      className="text-cyan-400"
                      title="إدارة الصلاحيات"
                      data-testid={`permissions-btn-${e.id}`}
                    >
                      <Shield className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => toggleEmployee(e.id)}
                      className={e.is_active ? "text-emerald-400" : "text-slate-500"}
                      title={e.is_active ? "تعطيل" : "تفعيل"}
                    >
                      {e.is_active ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => deleteEmployee(e.id, e.name)}
                      className="text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog: إضافة موظف */}
      <Dialog open={showCreate} onOpenChange={(o) => { if (!o) { setShowCreate(false); setCreatedCode(null); }}}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-emerald-400">
              {createdCode ? "✅ تم إنشاء كود الدعوة" : "➕ إضافة موظف جديد"}
            </DialogTitle>
          </DialogHeader>
          {createdCode ? (
            <div className="space-y-3 py-2">
              <div className="bg-slate-900/50 p-4 rounded-lg border border-emerald-500/30 text-center">
                <p className="text-slate-400 text-sm mb-2">كود الدعوة</p>
                <p className="text-3xl font-mono font-bold text-emerald-400 mb-2">{createdCode.code}</p>
                <p className="text-slate-400 text-sm">للموظف: <span className="text-white">{createdCode.name}</span></p>
              </div>
              {/* QR Code */}
              <div className="bg-white p-4 rounded-lg flex flex-col items-center" data-testid="created-invite-qr">
                <QRCodeSVG
                  value={inviteUrl(createdCode.code)}
                  size={180}
                  level="M"
                  includeMargin={false}
                />
                <p className="text-slate-700 text-xs mt-2 text-center">
                  📱 الموظف يمسح هذا الـ QR بكاميرا هاتفه لفتح صفحة التسجيل تلقائياً
                </p>
              </div>
              <div className="bg-amber-900/20 border border-amber-700/30 p-3 rounded-lg text-amber-300 text-sm">
                ⚠️ احفظ هذا الكود أو انسخه - راح يحتاجه الموظف للتسجيل
              </div>
              <Button onClick={() => sendWhatsApp(createdCode.code, createdCode.phone)} className="w-full bg-green-600 hover:bg-green-700">
                <svg className="w-4 h-4 me-2" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/></svg>
                إرسال عبر WhatsApp
              </Button>
              <Button onClick={() => copyCode(createdCode.code)} variant="outline" className="w-full border-slate-600 text-slate-300">
                <Copy className="w-4 h-4 me-2" />
                نسخ التعليمات
              </Button>
            </div>
          ) : (
            <div className="space-y-3 py-2">
              <div className="space-y-2">
                <Label className="text-slate-200">اسم الموظف</Label>
                <Input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className="bg-slate-900 border-slate-700 text-white"
                  placeholder="مثال: محمد علي"
                  data-testid="new-employee-name"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-200">المنصب</Label>
                <select
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-white"
                >
                  <option value="cashier">كاشير</option>
                  <option value="accountant">محاسب</option>
                  <option value="employee">موظف عام</option>
                  <option value="viewer">مشاهد فقط</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-200">رقم الموبايل (اختياري)</Label>
                <Input
                  value={newPhone}
                  onChange={(e) => setNewPhone(e.target.value)}
                  className="bg-slate-900 border-slate-700 text-white"
                  placeholder="07712345678"
                  data-testid="new-employee-phone"
                />
                <p className="text-xs text-slate-500">للإرسال السريع عبر WhatsApp</p>
              </div>
              <div className="bg-slate-900/40 p-3 rounded text-xs text-slate-400">
                💡 سيتم إنشاء كود دعوة فريد. أرسله للموظف ليستخدمه في تسجيل حسابه.
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            {createdCode ? (
              <Button onClick={() => { setShowCreate(false); setCreatedCode(null); }} className="bg-slate-700">
                إغلاق
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setShowCreate(false)} className="border-slate-600 text-slate-300">
                  إلغاء
                </Button>
                <Button
                  onClick={createInvite}
                  disabled={loading || !newName.trim()}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="confirm-create-invite"
                >
                  {loading ? "..." : "إنشاء كود"}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: عرض QR لكود موجود */}
      <Dialog open={!!qrInvite} onOpenChange={(o) => !o && setQrInvite(null)}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-cyan-400 flex items-center gap-2">
              <QrCode className="w-5 h-5" />
              QR Code للموظف
            </DialogTitle>
          </DialogHeader>
          {qrInvite && (
            <div className="space-y-3 py-2">
              <div className="bg-white p-4 rounded-lg flex flex-col items-center" data-testid="invite-qr-display">
                <QRCodeSVG
                  value={inviteUrl(qrInvite.code)}
                  size={220}
                  level="M"
                  includeMargin={false}
                />
                <p className="text-slate-700 text-xs mt-3 text-center font-bold">
                  {qrInvite.name}
                </p>
                <p className="text-slate-600 text-xs font-mono">
                  {qrInvite.code}
                </p>
              </div>
              <div className="bg-cyan-900/20 border border-cyan-700/30 p-3 rounded text-cyan-300 text-xs">
                📱 الموظف يفتح كاميرا هاتفه ويمسح الكود — يفتح صفحة التسجيل مع الكود مملوء تلقائياً
              </div>
              <Button
                onClick={() => { navigator.clipboard.writeText(inviteUrl(qrInvite.code)); toast.success("تم نسخ الرابط"); }}
                variant="outline"
                className="w-full border-slate-600 text-slate-300"
              >
                <Copy className="w-4 h-4 me-2" />
                نسخ الرابط المباشر
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ✨ Dialog: إدارة صلاحيات موظف */}
      <EmployeePermissionsDialog
        employee={permEmployee}
        token={token}
        open={!!permEmployee}
        onClose={() => setPermEmployee(null)}
        onSaved={fetchData}
      />
    </div>
  );
}
